import { useCallback } from "react";
import { pushStudioPath } from "@/lib/studio-navigation";
import { useStudioStore } from "@/store/studio-store";
import { useOpenCodeHandler } from "@/features/validation-bridge";
import type { RailItemId } from "../types";
import { activateStudioAuthorityTask } from "../studio-authority-activation";
import { useStudioProjectOutputKind } from "./artifact-mode-switcher";

function currentIdePath(projectId: string): string {
  return `/ide/${encodeURIComponent(projectId)}`;
}

function openCurrentProjectIde(): void {
  if (typeof window === "undefined") return;
  const projectId = useStudioStore.getState().currentProjectId;
  if (!projectId) return;
  const targetPath = currentIdePath(projectId);
  if (
    window.location.pathname === targetPath ||
    window.location.pathname.startsWith(`${targetPath}/`)
  )
    return;
  pushStudioPath(targetPath);
}

export function useGenerationNextActions() {
  const setActiveRailItem = useStudioStore((s) => s.setActiveRailItem);
  const openCodeSurface = useOpenCodeHandler();
  const outputKind = useStudioProjectOutputKind();

  const openRail = useCallback(
    (rail: RailItemId) => {
      if (rail === "preview" || rail === "deploy") {
        activateStudioAuthorityTask({
          domain: "primary",
          outputKind,
          railItem: rail,
        });
      } else {
        setActiveRailItem(rail);
      }
      openCurrentProjectIde();
    },
    [outputKind, setActiveRailItem],
  );

  const openCode = useCallback(() => {
    openCodeSurface();
    openCurrentProjectIde();
  }, [openCodeSurface]);

  return {
    openPreview: useCallback(() => openRail("preview"), [openRail]),
    openProjectOntology: useCallback(() => openRail("library"), [openRail]),
    openDeploy: useCallback(() => openRail("deploy"), [openRail]),
    openCode,
  };
}
